import React, { useEffect, useState } from 'react';
import Fuse from 'fuse.js';
import { PageContentLayout, PageHeaderLayout } from '../../../shared/layout';
import {
  TagOutlined,
  ToolOutlined,
  PlusOutlined,
  FilterOutlined,
} from '@ant-design/icons';
import { mainTitleIcon } from '../../../shared/globalStyling/style';
import {
  Button,
  Card,
  Col,
  Row,
  Select,
  Table,
  Tabs,
  Space,
  message,
  Typography,
  Tag,
} from 'antd';
import DocumentationAlert from './components/DocumentationAlert';
import MainTable from './components/MainTable';
import RequestAssistance from './components/RequestAssitance';
import { withRouter } from 'react-router-dom';
import CreateImpressionsTag from '../../SignalTags/main/components/CreateImpressionsTag';
import CreatePublisherDomainTag from '../../SignalTags/main/components/CreatePublsiherDomainTag';
import { formatNumber } from '../../../../core/utils/campaigns';
import MeasurementTableSearch from './components/MeasurementTableSearch';
import PublisherDomainTableSearch from './components/PublisherDomainTableSearch';
import ImpressionTableSearch from './components/ImpressionTableSearch';

const { Text } = Typography;

const performFuzzySearch = (data, searchKey, searchValue) => {
  const options = {
    keys: [searchKey],
    threshold: 0.3,
  };

  const fuse = new Fuse(data, options);
  return fuse.search(searchValue).map((result) => result.item);
};

const TagsMainView = ({
  history,
  location,
  requestAssistance,
  publishersTags,
  impressionsTags,
  measurementsTags,
  onImressionsTagCreate,
  refetch,
  isAttentionSignals,
  advertisers,
  testTag,
  basicTagActivity,
  clearBasicTagActivity,
}) => {
  document.title = 'Tags Tracking Main View';
  const params = new URLSearchParams(location.search);
  const status = params.get('status') ? params.get('status') : 'live';
  const tagId = params.get('tagId');
  const name = params.get('name');
  const publisherTagId = params.get('publisherTagId');
  const publisherName = params.get('publisherName');
  const impressionTagId = params.get('impressionTagId');
  const impressionName = params.get('impressionName');
  const initialTab = params.get('tab') || 'measurement';

  const [activeTab, setActiveTab] = useState(initialTab);
  const [filteredPublisherTags, setFilteredPublisherTags] = useState();
  const [filteredImpressionTags, setFilteredImpressionTags] = useState();
  const [filteredMeasurementTags, setFilteredMeasurementTags] = useState();

  useEffect(() => {
    setFilteredPublisherTags(publishersTags);
  }, [publishersTags]);

  useEffect(() => {
    setFilteredImpressionTags(impressionsTags);
  }, [impressionsTags]);

  useEffect(() => {
    setFilteredMeasurementTags(measurementsTags);
  }, [measurementsTags]);

  useEffect(() => {
    if (measurementsTags && tagId) {
      const searchTags = performFuzzySearch(measurementsTags, 'appId', tagId);
      setFilteredMeasurementTags(searchTags);
    }
  }, [measurementsTags, history, tagId]);

  useEffect(() => {
    if (measurementsTags && name) {
      const searchTags = performFuzzySearch(measurementsTags, 'name', name);
      setFilteredMeasurementTags(searchTags);
    }
  }, [measurementsTags, history, name]);

  useEffect(() => {
    if (publishersTags && publisherTagId) {
      const searchTags = performFuzzySearch(publishersTags, 'tagId', publisherTagId);
      setFilteredPublisherTags(searchTags);
    }
  }, [publishersTags, history, publisherTagId]);

  useEffect(() => {
    if (publishersTags && publisherName) {
      const searchTags = performFuzzySearch(publishersTags, 'name', publisherName);
      setFilteredPublisherTags(searchTags);
    }
  }, [publishersTags, history, publisherName]);

  useEffect(() => {
    if (impressionsTags && impressionTagId) {
      const searchTags = performFuzzySearch(impressionsTags, 'tagId', impressionTagId);
      setFilteredImpressionTags(searchTags);
    }
  }, [impressionsTags, history, impressionTagId]);

  useEffect(() => {
    if (impressionsTags && impressionName) {
      const searchTags = performFuzzySearch(impressionsTags, 'name', impressionName);
      setFilteredImpressionTags(searchTags);
    }
  }, [impressionsTags, history, impressionName]);

  useEffect(() => {
    if (basicTagActivity) {
      message.success('Signal Tested Successfully', 3);
      clearBasicTagActivity();
    } else if (basicTagActivity === '') {
      message.error('Error Testing Signal: cannot fetch tag activity');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicTagActivity]);

  const publisherColumn = [
    {
      title: 'Tag Name',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Advertiser',
      dataIndex: 'advertiser',
      key: 'advertiser',
      width: '15%',
      ellipsis: true,
      render: (text) => <Text type="secondary">{text}</Text>,
    },
    {
      title: 'Domain',
      dataIndex: 'domain',
      key: 'domain',
      width: '20%',
      ellipsis: true,
      render: (text) => (
        <Text
          style={{
            fontFamily: 'monospace',
            fontSize: 12,
            backgroundColor: '#f5f5f5',
            padding: '2px 6px',
            borderRadius: 4,
          }}
        >
          {text}
        </Text>
      ),
    },
    {
      title: 'Measured Events',
      key: 'pageViews',
      width: '15%',
      render: (record) => (
        <div
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '6px 12px',
            borderRadius: 6,
            backgroundColor: record.color === 'success' ? '#f6ffed' : '#fafafa',
            border: `1px solid ${record.color === 'success' ? '#b7eb8f' : '#d9d9d9'}`,
          }}
        >
          <Text style={{ fontSize: 11, color: '#8c8c8c', textTransform: 'uppercase' }}>
            Page Views
          </Text>
          <Text strong style={{ fontSize: 14, color: record.color === 'success' ? '#52c41a' : '#8c8c8c' }}>
            {formatNumber(record.pageViews)}
          </Text>
        </div>
      ),
    },
    {
      title: 'Tag ID',
      dataIndex: 'tagId',
      key: 'tagId',
      width: '20%',
      render: (text) => (
        <Typography.Text
          copyable={{ tooltips: ['Copy', 'Copied!'] }}
          style={{
            fontFamily: 'monospace',
            fontSize: 11,
            backgroundColor: '#f5f5f5',
            padding: '4px 8px',
            borderRadius: 4,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {text}
        </Typography.Text>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: '10%',
      render: (record) => (
        <Button
          type="primary"
          icon={<TagOutlined />}
          size="small"
          onClick={async (event) => {
            event.stopPropagation();
            clearBasicTagActivity();
            testTag({
              appId: record.tagId,
              orgId: record.orgId,
              tagId: record.id,
            });
            message.info(`Signal Tag Test for ${record.name} initiated.`);
          }}
          style={{ borderRadius: 6 }}
        >
          Test
        </Button>
      ),
    },
  ];

  const impressionColumn = [
    {
      title: 'Tag Name',
      dataIndex: 'name',
      key: 'name',
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Platform',
      dataIndex: 'dsp',
      key: 'dsp',
      render: (text) => (
        <Tag color="blue" style={{ borderRadius: 4 }}>
          {text}
        </Tag>
      ),
    },
    {
      title: 'Measured Events',
      key: 'impressions',
      width: '15%',
      render: (record) => (
        <div
          style={{
            display: 'inline-flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '6px 12px',
            borderRadius: 6,
            backgroundColor: record.color === 'success' ? '#f6ffed' : '#fafafa',
            border: `1px solid ${record.color === 'success' ? '#b7eb8f' : '#d9d9d9'}`,
          }}
        >
          <Text style={{ fontSize: 11, color: '#8c8c8c', textTransform: 'uppercase' }}>
            Impressions
          </Text>
          <Text strong style={{ fontSize: 14, color: record.color === 'success' ? '#52c41a' : '#8c8c8c' }}>
            {formatNumber(record.impressions)}
          </Text>
        </div>
      ),
    },
    {
      title: 'Tag ID',
      dataIndex: 'tagId',
      key: 'tagId',
      render: (text) => (
        <Typography.Text
          copyable={{ tooltips: ['Copy', 'Copied!'] }}
          style={{
            fontFamily: 'monospace',
            fontSize: 11,
            backgroundColor: '#f5f5f5',
            padding: '4px 8px',
            borderRadius: 4,
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {text}
        </Typography.Text>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: '10%',
      render: (record) => (
        <Button
          type="primary"
          icon={<TagOutlined />}
          size="small"
          onClick={async (event) => {
            event.stopPropagation();
            clearBasicTagActivity();
            testTag({
              appId: record.tagId,
              orgId: record.orgId,
              tagId: record.id,
            });
            message.info(`Signal Tag Test for ${record.name} initiated.`);
          }}
          style={{ borderRadius: 6 }}
        >
          Test
        </Button>
      ),
    },
  ];

  const handleStatusChange = (val, tab) => {
    if (status) {
      params.delete('status');
    }
    params.set('tab', tab);
    params.append('status', val);
    history.push({
      pathname: location.pathname,
      search: `?${params.toString()}`,
    });
  };

  const renderMeasurementTagsContent = () => (
    <div>
      {/* Controls Section */}
      <Card
        style={{
          marginBottom: 16,
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}
        bodyStyle={{ padding: '16px 24px' }}
      >
        <Row justify="space-between" align="middle">
          <Col>
            <Space size={12}>
              <Select
                value={status}
                style={{ width: 130 }}
                onChange={(val) => handleStatusChange(val, 'measurement')}
                suffixIcon={<FilterOutlined />}
              >
                <Select.Option value="live">
                  <Space>
                    <span style={{ color: '#52c41a' }}>●</span> Live
                  </Space>
                </Select.Option>
                <Select.Option value="disabled">
                  <Space>
                    <span style={{ color: '#d9d9d9' }}>●</span> Archived
                  </Space>
                </Select.Option>
              </Select>
              <MeasurementTableSearch />
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => history.push(`/tags/manager?source=measurement`)}
              style={{ borderRadius: 6 }}
            >
              Create New Tag
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Documentation Alert */}
      <DocumentationAlert />

      {/* Table Section */}
      <Card
        style={{
          borderRadius: 12,
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          marginTop: 16,
        }}
        bodyStyle={{ padding: 0 }}
      >
        <MainTable
          tags={filteredMeasurementTags}
          testTag={testTag}
          isAttentionSignals={isAttentionSignals}
        />
      </Card>
    </div>
  );

  const tabItems = [
    {
      key: 'measurement',
      label: (
        <span style={{ padding: '0 8px' }}>
          <TagOutlined style={{ marginRight: 8 }} />
          Measurement Tags
        </span>
      ),
      children: renderMeasurementTagsContent(),
    },
    {
      key: 'domain',
      label: (
        <span style={{ padding: '0 8px' }}>
          <TagOutlined style={{ marginRight: 8 }} />
          Publisher Domain Tags
        </span>
      ),
      children: (
        <Card
          style={{ borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}
          title={
            <Space>
              <TagOutlined />
              <span>Publisher Domain Tags</span>
            </Space>
          }
          extra={
            <Space size={12}>
              <Select
                value={status}
                style={{ width: 130 }}
                onChange={(val) => handleStatusChange(val, 'domain')}
              >
                <Select.Option value="live">
                  <Space>
                    <span style={{ color: '#52c41a' }}>●</span> Live
                  </Space>
                </Select.Option>
                <Select.Option value="disabled">
                  <Space>
                    <span style={{ color: '#d9d9d9' }}>●</span> Archived
                  </Space>
                </Select.Option>
              </Select>
              <PublisherDomainTableSearch />
              <CreatePublisherDomainTag />
            </Space>
          }
        >
          <Table
            columns={publisherColumn}
            dataSource={filteredPublisherTags}
            rowKey={(record) => record.id}
            onRow={(record) => ({
              onClick: () => history.push(`/tags/details/${record.id}?source=publisher`),
              style: { cursor: 'pointer' },
            })}
            pagination={{
              showSizeChanger: filteredPublisherTags?.length > 10,
                          }}
          />
        </Card>
      ),
    },
    {
      key: 'impressions',
      label: (
        <span style={{ padding: '0 8px' }}>
          <TagOutlined style={{ marginRight: 8 }} />
          Impression Tags
        </span>
      ),
      children: (
        <Card
          style={{ borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}
          title={
            <Space>
              <TagOutlined />
              <span>Impression Tags</span>
            </Space>
          }
          extra={
            <Space size={12}>
              <Select
                value={status}
                style={{ width: 130 }}
                onChange={(val) => handleStatusChange(val, 'impressions')}
              >
                <Select.Option value="live">
                  <Space>
                    <span style={{ color: '#52c41a' }}>●</span> Live
                  </Space>
                </Select.Option>
                <Select.Option value="disabled">
                  <Space>
                    <span style={{ color: '#d9d9d9' }}>●</span> Archived
                  </Space>
                </Select.Option>
              </Select>
              <ImpressionTableSearch />
              <CreateImpressionsTag
                onSubmit={onImressionsTagCreate}
                refetch={refetch}
                advertisers={advertisers}
              />
            </Space>
          }
        >
          <Table
            columns={impressionColumn}
            dataSource={filteredImpressionTags}
            rowKey={(record) => record.id}
            onRow={(record) => ({
              onClick: () => history.push(`/tags/details/${record.id}?source=impression`),
              style: { cursor: 'pointer' },
            })}
            pagination={{
              showSizeChanger: filteredImpressionTags?.length > 10,
                          }}
          />
        </Card>
      ),
    },
  ];

  return (
    <React.Fragment>
      <PageHeaderLayout
        title={'Tags'}
        titleIcon={<TagOutlined style={mainTitleIcon} />}
        rightContent={
          <Space>
            <RequestAssistance onRequestAssistance={requestAssistance} />
            <Button
              type="default"
              icon={<ToolOutlined />}
              onClick={() => history.push('/tag_help_center')}
              style={{ borderRadius: 6 }}
            >
              Tag Help Center
            </Button>
          </Space>
        }
      />
      <PageContentLayout>
        {isAttentionSignals ? (
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            style={{
              background: 'transparent',
            }}
            tabBarStyle={{
              marginBottom: 24,
              borderBottom: '1px solid #f0f0f0',
            }}
          />
        ) : (
          renderMeasurementTagsContent()
        )}
      </PageContentLayout>
    </React.Fragment>
  );
};

export default withRouter(TagsMainView);
